import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS, LOGO_URL } from "@/data/siteData";
import { CTAButton } from "@/components/shared/CTAButton";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-cream/85 backdrop-blur-xl shadow-[0_1px_0_0_rgba(10,25,47,0.08)] py-3" : "bg-transparent py-6"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3.5">
          <img src={LOGO_URL} alt="Mithra Jobs logo" className="h-11 w-11" />
          <span
            className={`font-heading text-2xl font-semibold tracking-tight transition-colors duration-300 ${
              scrolled ? "text-navy" : "text-cream"
            }`}
          >
            Mithra Jobs
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors duration-300 hover:text-accent ${
                  isActive ? "text-accent" : scrolled ? "text-navy/80" : "text-cream/85"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <CTAButton to="/contact-us#enquiry" variant="primary" testId="header-cta-find-teacher">
            Find the Right Teacher
          </CTAButton>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(true)}
          className={`lg:hidden p-2 -mr-2 transition-colors duration-300 ${scrolled ? "text-navy" : "text-cream"}`}
          aria-label="Open menu"
        >
          <List size={26} weight="bold" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-navy lg:hidden"
          >
            <div className="flex items-center justify-between px-6 py-6">
              <span className="font-heading text-lg font-semibold text-cream">Mithra Jobs</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="text-cream p-2"
                aria-label="Close menu"
              >
                <X size={26} weight="bold" />
              </button>
            </div>
            <nav className="flex flex-col gap-1 px-6 mt-6">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={link.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block py-4 text-3xl font-heading text-cream/90 border-b border-white/10"
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-6 mt-10">
              <CTAButton
                to="/contact-us#enquiry"
                variant="primary"
                onClick={() => setOpen(false)}
                testId="mobile-menu-cta"
              >
                Find the Right Teacher
              </CTAButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
